import { Button } from '@oysterjs/ui/button';

import { Action, Resource } from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from './current-user';
import { FeatureDocCallout } from './feature-doc-callout';
import { usePermissions } from './permissions';
import { hasLimitedUsageAllotment } from './usage-allotment';

type UsageAllotmentFeature = 'triggers' | 'outboundVoice';

const CONTACT_SALES_URL = 'https://meetings.hubspot.com/vic-yeh';

const ALLOTMENT_COPY: Record<UsageAllotmentFeature, { title: string; description: string }> = {
  triggers: {
    title: 'Your current plan includes 100 triggered events',
    description: 'Upgrade to a usage-based plan to keep using these features without limits.'
  },
  outboundVoice: {
    title: 'Your current plan includes 250 outbound voice minutes',
    description: 'Upgrade to a usage-based plan to keep using these features without limits.'
  }
};

type UsageAllotmentBannerProps = {
  feature: UsageAllotmentFeature;
  className?: string;
};

const UsageAllotmentBannerContent = ({ feature, className }: UsageAllotmentBannerProps) => {
  const canContactSales = usePermissions().hasPermission(Resource.Subscription, Action.Update);
  const { title, description } = ALLOTMENT_COPY[feature];

  return (
    <FeatureDocCallout
      variant="sky"
      className={className}
      title={title}
      description={canContactSales ? description : `${description} Ask an admin to contact sales.`}
      action={
        canContactSales ? (
          <Button
            color="white"
            href={CONTACT_SALES_URL}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 self-start"
          >
            Contact Sales
          </Button>
        ) : null
      }
    />
  );
};

export const UsageAllotmentBanner = (props: UsageAllotmentBannerProps) => {
  const agencyUser = useOptionalCurrentAgencyUser();
  if (
    !hasLimitedUsageAllotment(
      agencyUser?.agency.subscription,
      agencyUser?.settingsUsageTabEnabled ?? false
    )
  ) {
    return null;
  }
  return <UsageAllotmentBannerContent {...props} />;
};
